import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { apiPrivate } from "@/lib/api";
import { Briefcase, TrendingUp, AlertTriangle, Clock, CheckCircle2, XCircle, ShoppingBag, Loader2, ArrowUpRight, Mail, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (v) => v == null ? "—" : `€${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 })}`;
const fmtQuote = (v) => v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const fmtPct = (v) => v == null ? "—" : `${Number(v).toFixed(4)}%`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { dateStyle: "medium" }); } catch { return iso; } };

async function downloadDisclaimerPdf(filename) {
    try {
        const res = await apiPrivate.get("/market/disclaimer-pdf", { responseType: "blob" });
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "disclaimer-bacheca-annunci-soci.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (e) {
        toast.error(e?.response?.status === 404 ? "Il PDF non è stato ancora caricato dall'amministrazione." : "Errore nel download del PDF");
    }
}

export default function Investimento() {
    const { user } = useAuth();
    const [holdings, setHoldings] = useState([]);
    const [userLegacy, setUserLegacy] = useState(null);
    const [listings, setListings] = useState([]);
    const [preemptions, setPreemptions] = useState([]);
    const [disclaimerStatus, setDisclaimerStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sellFor, setSellFor] = useState(null); // holding aggregato oggetto della modal
    const [showCreateListing, setShowCreateListing] = useState(false); // maschera generica "crea annuncio"

    const reload = async () => {
        setLoading(true);
        try {
            const [me, myh, myl, myp, disc] = await Promise.all([
                apiPrivate.get("/soci/me"),
                apiPrivate.get("/soci/me/holdings").catch(() => ({ data: [] })),
                apiPrivate.get("/market/my-listings").catch(() => ({ data: [] })),
                apiPrivate.get("/market/preemption/my").catch(() => ({ data: [] })),
                apiPrivate.get("/market/disclaimer").catch(() => ({ data: null })),
            ]);
            setHoldings(myh.data || []);
            // Preservo i campi legacy dell'utente per il fallback UI (piattaforma migrata: alcuni
            // utenti hanno solo quota_categories / valore_nominale_quota / percentuale_possesso)
            setUserLegacy({
                quota_categories: me.data.quota_categories || [],
                valore_nominale_quota: me.data.valore_nominale_quota,
                percentuale_possesso: me.data.percentuale_possesso,
            });
            setListings(myl.data || []);
            setPreemptions(myp.data || []);
            setDisclaimerStatus(disc.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { reload(); }, []);

    // Aggrego holdings per categoria (un utente può avere >1 holding stessa categoria)
    let byCategory = holdings.reduce((acc, h) => {
        if (!acc[h.categoria]) acc[h.categoria] = { categoria: h.categoria, valore: 0, percentuale: 0, count: 0, holdings: [] };
        acc[h.categoria].valore += Number(h.valore_nominale || 0);
        acc[h.categoria].percentuale += Number(h.percentuale_possesso || 0);
        acc[h.categoria].count += 1;
        acc[h.categoria].holdings.push(h);
        return acc;
    }, {});
    // Fallback: se quote_holdings è vuoto ma l'utente ha campi aggregati legacy,
    // costruisco pseudo-holdings distribuendo il possesso equamente tra le categorie
    // possedute. Necessario per utenti migrati che non hanno ancora quote_holdings valorizzato.
    if (Object.keys(byCategory).length === 0 && userLegacy?.quota_categories?.length > 0) {
        const n = userLegacy.quota_categories.length;
        const perCat = Number(userLegacy.valore_nominale_quota || 0) / n;
        const perPct = Number(userLegacy.percentuale_possesso || 0) / n;
        byCategory = userLegacy.quota_categories.reduce((acc, cat) => {
            acc[cat] = {
                categoria: cat,
                valore: perCat,
                percentuale: perPct,
                count: 1,
                holdings: [],
                legacy: true,
            };
            return acc;
        }, {});
    }
    const categories = Object.values(byCategory).sort((a, b) => a.categoria.localeCompare(b.categoria));

    // Stato per categoria: attiva listing, prelazione, ecc.
    const listingByCat = Object.fromEntries(listings.map(l => [l.quota_category, l]));
    // Mappa prelazioni per categoria: prendo la più recente (backend ritorna sort desc)
    // Includo anche le "rejected" per informare l'utente del rifiuto e permettere il retry.
    // Escludo solo quelle già consumed (annuncio già pubblicato e chiuso).
    const preemptionByCat = {};
    preemptions
        .filter(p => !p.consumed)
        .forEach(p => {
            if (!preemptionByCat[p.quota_category]) preemptionByCat[p.quota_category] = p;
        });

    const totalValore = categories.reduce((s, c) => s + c.valore, 0);

    if (loading) {
        return <AppLayout><div className="p-12 text-center text-zinc-500"><Loader2 className="h-6 w-6 animate-spin inline" /></div></AppLayout>;
    }

    return (
        <AppLayout>
            <div className="max-w-[1400px] mx-auto" data-testid="investimento-page">
                <span className="label-tag">— Portafoglio</span>
                <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tighter">
                    Il mio <span className="font-serif-italic text-acid">investimento</span>.
                </h1>
                <p className="text-zinc-400 mt-3 max-w-2xl">Fotografia attuale della tua posizione da socio e strumenti per metterla in vendita categoria per categoria.</p>

                {/* KPI totali */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-10">
                    <div className="bento p-5" data-testid="kpi-valore">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Numero quote totali</div>
                        <div className="text-4xl font-display tracking-tighter mt-2 text-bayside">{fmtQuote(totalValore)}</div>
                    </div>
                    <div className="bento p-5" data-testid="kpi-categorie">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Categorie possedute</div>
                        <div className="text-4xl font-display tracking-tighter mt-2">{categories.length}</div>
                        <div className="text-xs text-zinc-500 mt-1">{categories.map(c => `Quota ${c.categoria}`).join(" · ")}</div>
                    </div>
                </div>

                {/* Cards per categoria — SOLO dettagli quota (dettagli separati dalle richieste/annunci) */}
                {categories.length === 0 ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-10" data-testid="no-holdings">
                        <Briefcase className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        Nessuna quota di investimento registrata. Contatta il team se ritieni sia un errore.
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-4 mt-10">
                        {categories.map(cat => (
                            <CategoryCard
                                key={cat.categoria}
                                cat={cat}
                                available={computeAvailable(cat, listings, preemptions)}
                                hasListing={!!listingByCat[cat.categoria]}
                            />
                        ))}
                    </div>
                )}

                {/* Sezione separata: creazione annuncio bacheca annunci soci */}
                {categories.length > 0 && (
                    <div className="mt-16 pt-10 border-t border-zinc-900" data-testid="create-listing-section">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="max-w-xl">
                                <span className="label-tag text-magenta">— Bacheca annunci soci</span>
                                <h2 className="font-display text-3xl lg:text-4xl mt-2 tracking-tighter">
                                    Crea un <span className="font-serif-italic text-gradient-tropical">annuncio</span>.
                                </h2>
                                <p className="text-zinc-400 mt-3 text-sm">
                                    Metti in vendita una parte del tuo possesso: per Quota A la richiesta di prelazione parte automaticamente dopo aver scelto l'importo. La disponibilità tiene già conto degli annunci attivi e delle richieste di prelazione in essere.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowCreateListing(true)}
                                className="btn-rebel"
                                data-testid="open-create-listing"
                            >
                                <ShoppingBag className="h-4 w-4" /> Crea annuncio
                            </button>
                        </div>

                        {/* Annunci pubblicati (attivi) */}
                        <div className="mt-10">
                            <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-grotesk mb-3">Annunci pubblicati</h3>
                            {listings.filter(l => l.is_active).length === 0 ? (
                                <div className="bento p-6 text-sm text-zinc-500" data-testid="no-listings">Nessun annuncio pubblicato al momento.</div>
                            ) : (
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-listings-grid">
                                    {listings.filter(l => l.is_active).map(l => (
                                        <ListingCard key={l.id} listing={l} onWithdraw={reload} />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Prelazioni attive/storiche recenti */}
                        <div className="mt-10">
                            <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-grotesk mb-3">Richieste di prelazione (Quota A)</h3>
                            {preemptions.filter(p => !p.consumed).length === 0 ? (
                                <div className="bento p-6 text-sm text-zinc-500" data-testid="no-preemptions">Nessuna richiesta di prelazione attiva.</div>
                            ) : (
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-preemptions-grid">
                                    {preemptions.filter(p => !p.consumed).map(p => (
                                        <PreemptionCard key={p.id} preemption={p} disclaimerStatus={disclaimerStatus} onChange={reload} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {sellFor && (
                    <SellModal
                        category={sellFor}
                        disclaimerAccepted={disclaimerStatus?.accepted}
                        disclaimerText={disclaimerStatus?.text}
                        onClose={() => setSellFor(null)}
                        onSuccess={() => { setSellFor(null); reload(); }}
                    />
                )}

                {showCreateListing && (
                    <CreateListingModal
                        categories={categories}
                        listings={listings}
                        preemptions={preemptions}
                        listingByCat={listingByCat}
                        preemptionByCat={preemptionByCat}
                        disclaimerStatus={disclaimerStatus}
                        onClose={() => setShowCreateListing(false)}
                        onSuccess={() => { setShowCreateListing(false); reload(); }}
                        onRequestPreemption={reload}
                    />
                )}
            </div>
        </AppLayout>
    );
}

// Calcola disponibilità (% e €) per una categoria, sottraendo listings attivi e prelazioni in essere.
function computeAvailable(cat, listings, preemptions) {
    const usedPct = listings
        .filter(l => l.is_active && l.quota_category === cat.categoria)
        .reduce((s, l) => s + (Number(l.quantity) || 0), 0)
        + preemptions
            .filter(p => !p.consumed && p.quota_category === cat.categoria && (p.status === "pending" || p.status === "approved"))
            .reduce((s, p) => s + (Number(p.quantity) || 0), 0);
    const usedValue = listings
        .filter(l => l.is_active && l.quota_category === cat.categoria)
        .reduce((s, l) => s + (Number(l.valore_richiesto) || 0), 0)
        + preemptions
            .filter(p => !p.consumed && p.quota_category === cat.categoria && (p.status === "pending" || p.status === "approved"))
            .reduce((s, p) => s + (Number(p.valore_richiesto) || 0), 0);
    const remainingPct = Math.max(0, Number(cat.percentuale || 0) - usedPct);
    const remainingValue = Math.max(0, Number(cat.valore || 0) - usedValue);
    return { usedPct, remainingPct, usedValue, remainingValue };
}

function CategoryCard({ cat, available, hasListing }) {
    return (
        <div className="bento p-6" data-testid={`cat-card-${cat.categoria}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <span className="px-3 py-1 rounded-full bg-bayside/15 text-bayside font-semibold text-sm">Quota {cat.categoria}</span>
                    {cat.count > 1 && <span className="ml-2 text-xs text-zinc-500">({cat.count} lotti)</span>}
                </div>
                {hasListing && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-acid/20 text-acid flex items-center gap-1">
                        <ShoppingBag className="h-3 w-3" /> Annuncio attivo
                    </span>
                )}
            </div>
            <div className="grid grid-cols-1 gap-4 mt-4">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Numero quote</div>
                    <div className="text-2xl font-display tracking-tighter mt-1 text-bayside">{fmtQuote(cat.valore)}</div>
                </div>
            </div>

            {cat.holdings.some(h => h.data_acquisto) && (
                <div className="mt-3 text-xs text-zinc-500">
                    Acquisti: {cat.holdings.filter(h => h.data_acquisto).map(h => fmtDate(h.data_acquisto)).join(" · ")}
                </div>
            )}

            {/* Disponibilità residua (dettaglio quota, indipendente dagli annunci) */}
            <div className="mt-4 pt-4 border-t border-zinc-800 text-[11px]" data-testid={`availability-${cat.categoria}`}>
                <div className="uppercase tracking-widest text-zinc-500 font-grotesk">Quote libere per annunci</div>
                <div className="text-sm text-bayside mt-1 font-mono">{fmtQuote(available.remainingValue)}</div>
                <div className="text-[10px] text-zinc-500">Impegnate in annunci/prelazioni: {fmtQuote(available.usedValue)}</div>
            </div>
        </div>
    );
}

function ListingCard({ listing, onWithdraw }) {
    const [busy, setBusy] = useState(false);
    const withdraw = async () => {
        if (!window.confirm("Ritirare l'inserzione dalla Bacheca annunci soci?")) return;
        setBusy(true);
        try {
            await apiPrivate.delete(`/market/listings/${listing.id}`);
            toast.success("Inserzione ritirata");
            onWithdraw?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };
    return (
        <div className="bento p-4" data-testid={`listing-card-${listing.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-xs bg-bayside/15 text-bayside">Quota {listing.quota_category}</span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-acid/15 text-acid">Pubblicato</span>
            </div>
            <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Quote in vendita</div>
                {listing.numero_quote != null ? (
                    <div className="text-lg font-display text-magenta" data-testid={`listing-quote-count-${listing.id}`}>
                        {fmtQuote(listing.numero_quote)}
                    </div>
                ) : (
                    <div className="text-xs text-zinc-500 italic font-grotesk">Numero quote non disponibile</div>
                )}
            </div>
            {listing.note && <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{listing.note}</p>}
            {listing.disclaimer_accepted_at && (
                <p className="text-[10px] text-zinc-600 mt-2 font-grotesk" data-testid={`listing-disclaimer-${listing.id}`}>
                    ✓ Disclaimer accettato {fmtDate(listing.disclaimer_accepted_at)}
                </p>
            )}
            <button onClick={withdraw} disabled={busy} className="btn-ghost text-xs text-vermillion w-full justify-center mt-3" data-testid={`withdraw-listing-${listing.id}`}>
                <XCircle className="h-3.5 w-3.5" /> Ritira inserzione
            </button>
        </div>
    );
}

function PreemptionCard({ preemption, disclaimerStatus, onChange }) {
    const [busy, setBusy] = useState(false);
    const [showPublish, setShowPublish] = useState(false);
    const [disclaimerChecked, setDisclaimerChecked] = useState(false);
    const retry = async () => {
        setBusy(true);
        try {
            await apiPrivate.post("/market/preemption", {
                quota_category: preemption.quota_category,
                quantity: preemption.quantity || undefined,
                valore_richiesto: preemption.valore_richiesto || undefined,
            });
            toast.success("Nuova richiesta di prelazione inviata");
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };
    const publish = async () => {
        if (!disclaimerChecked) { toast.error("Accetta il disclaimer per pubblicare"); return; }
        setBusy(true);
        try {
            await apiPrivate.post("/market/listings", {
                quota_category: preemption.quota_category,
                quantity: Number(preemption.quantity),
                valore_richiesto: Number(preemption.valore_richiesto || 0),
                note: null,
                disclaimer_accepted: true,
            });
            toast.success("Annuncio pubblicato — disclaimer registrato");
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore pubblicazione");
        } finally { setBusy(false); }
    };
    const statusMeta = {
        pending: { label: "In attesa", tone: "bg-magenta/15 text-magenta", icon: Clock },
        approved: { label: "Approvata", tone: "bg-bayside/15 text-bayside", icon: CheckCircle2 },
        rejected: { label: "Respinta", tone: "bg-vermillion/15 text-vermillion", icon: XCircle },
    }[preemption.status] || { label: preemption.status, tone: "bg-zinc-500/15 text-zinc-400", icon: Clock };
    const Icon = statusMeta.icon;
    const publishable = preemption.status === "approved" && preemption.quantity != null && preemption.valore_richiesto != null;
    return (
        <div className="bento p-4" data-testid={`preemption-card-${preemption.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-xs bg-bayside/15 text-bayside">Quota {preemption.quota_category}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${statusMeta.tone}`}>
                    <Icon className="h-3 w-3" /> {statusMeta.label}
                </span>
            </div>
            {(preemption.numero_quote != null || preemption.quantity != null) && (
                <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Quote richieste</div>
                    {preemption.numero_quote != null ? (
                        <div className="text-sm font-mono text-magenta" data-testid={`preemption-quote-count-${preemption.id}`}>
                            {fmtQuote(preemption.numero_quote)}
                        </div>
                    ) : (
                        <div className="text-xs text-zinc-500 italic font-grotesk">Numero quote non disponibile</div>
                    )}
                </div>
            )}
            <div className="text-[11px] text-zinc-500 mt-2">Richiesta il {fmtDate(preemption.created_at)}</div>
            {preemption.status === "rejected" && preemption.admin_note && (
                <div className="mt-2 text-[11px] text-zinc-400 bg-vermillion/5 p-2 rounded-lg">
                    <span className="text-vermillion">Motivo:</span> {preemption.admin_note}
                </div>
            )}
            {preemption.status === "approved" && (
                <>
                    {!showPublish ? (
                        <div className="mt-3 flex flex-col gap-2">
                            <p className="text-[11px] text-bayside">La prelazione è stata approvata: puoi pubblicare l'annuncio esattamente per l'importo indicato.</p>
                            {publishable ? (
                                <button onClick={() => setShowPublish(true)} disabled={busy} className="btn-rebel w-full justify-center text-xs" data-testid={`publish-preemption-${preemption.id}`}>
                                    <ShoppingBag className="h-3.5 w-3.5" /> Pubblica annuncio
                                </button>
                            ) : (
                                <p className="text-[10px] text-vermillion">Importo non definito: usa il modulo "Crea annuncio" per completare la pubblicazione.</p>
                            )}
                        </div>
                    ) : (
                        <div className="mt-3 space-y-2 border-t border-zinc-800 pt-3" data-testid={`publish-disclaimer-block-${preemption.id}`}>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">Disclaimer obbligatorio · versione <span className="text-magenta">{disclaimerStatus?.version || "—"}</span></div>
                            {disclaimerStatus?.pdf_available ? (
                                <button
                                    onClick={() => downloadDisclaimerPdf(disclaimerStatus.pdf_filename)}
                                    className="btn-ghost text-xs w-full justify-center"
                                    data-testid={`publish-disclaimer-download-${preemption.id}`}
                                >
                                    <Download className="h-3.5 w-3.5" /> Scarica e leggi il PDF del disclaimer
                                </button>
                            ) : (
                                <p className="text-[11px] text-vermillion italic">Il PDF del disclaimer non è ancora stato caricato dall'amministrazione. Contatta il team.</p>
                            )}
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={disclaimerChecked}
                                    onChange={e => setDisclaimerChecked(e.target.checked)}
                                    disabled={!disclaimerStatus?.pdf_available}
                                    className="mt-0.5 accent-magenta h-4 w-4"
                                    data-testid={`publish-disclaimer-check-${preemption.id}`}
                                />
                                <span className="text-[11px] text-zinc-300">Dichiaro di aver scaricato, letto e accettato il PDF del disclaimer. La data verrà registrata e associata all'annuncio.</span>
                            </label>
                            <div className="flex gap-2 pt-1">
                                <button onClick={() => { setShowPublish(false); setDisclaimerChecked(false); }} className="btn-ghost text-xs flex-1 justify-center">Annulla</button>
                                <button onClick={publish} disabled={!disclaimerChecked || busy || !disclaimerStatus?.pdf_available} className="btn-rebel text-xs flex-1 justify-center" data-testid={`confirm-publish-preemption-${preemption.id}`}>
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
                                    Pubblica
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
            {preemption.status === "rejected" && (
                <button onClick={retry} disabled={busy} className="btn-ghost text-xs w-full justify-center mt-3" data-testid={`retry-preemption-${preemption.id}`}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    Invia nuova richiesta
                </button>
            )}
        </div>
    );
}


function SellModal({ category, disclaimerAccepted, disclaimerText, onClose, onSuccess }) {
    const [step, setStep] = useState(disclaimerAccepted ? "form" : "disclaimer");
    const [disclaimerMeta, setDisclaimerMeta] = useState(null);
    const [saving, setSaving] = useState(false);

    // Prezzo unitario di riferimento (€ per 1% di quota della categoria)
    const unitPrice = category.percentuale > 0 ? category.valore / category.percentuale : 0;
    // Quote per 1% di possesso (es. 25000 quote / 1,25% = 20000 quote per 1%)
    const quotePerPercent = category.percentuale > 0 ? category.valore / category.percentuale : 0;

    // Precompilazione: massimo cedibile della categoria
    const maxPct = Number(category.percentuale || 0);
    const [form, setForm] = useState({
        quantity: maxPct,
        quote_amount: Number(category.valore || 0),
        valore_richiesto: Number(category.valore || 0),
        note: "",
    });
    // Ultimo campo editato dall'utente per il calcolo automatico
    const [lastEdited, setLastEdited] = useState(null);

    useEffect(() => {
        apiPrivate.get("/market/disclaimer").then(r => setDisclaimerMeta(r.data)).catch(() => { });
    }, []);

    const handleQuantityChange = (v) => {
        setLastEdited("quantity");
        setForm(f => {
            const next = { ...f, quantity: v };
            const num = parseFloat(v);
            if (!isNaN(num) && num >= 0) {
                if (unitPrice > 0) next.valore_richiesto = +(num * unitPrice).toFixed(2);
                if (quotePerPercent > 0) next.quote_amount = +(num * quotePerPercent).toFixed(2);
            }
            return next;
        });
    };
    const handleValoreChange = (v) => {
        setLastEdited("valore");
        setForm(f => {
            const next = { ...f, valore_richiesto: v };
            const num = parseFloat(v);
            if (!isNaN(num) && num >= 0) {
                if (unitPrice > 0) next.quantity = +(num / unitPrice).toFixed(6);
                if (unitPrice > 0 && quotePerPercent > 0) {
                    next.quote_amount = +((num / unitPrice) * quotePerPercent).toFixed(2);
                }
            }
            return next;
        });
    };
    const handleQuoteChange = (v) => {
        setLastEdited("quote");
        setForm(f => {
            const next = { ...f, quote_amount: v };
            const num = parseFloat(v);
            if (!isNaN(num) && num >= 0 && quotePerPercent > 0) {
                const pct = num / quotePerPercent;
                next.quantity = +pct.toFixed(6);
                if (unitPrice > 0) next.valore_richiesto = +(pct * unitPrice).toFixed(2);
            }
            return next;
        });
    };
    const resetToMax = () => {
        setForm({
            quantity: maxPct,
            quote_amount: Number(category.valore || 0),
            valore_richiesto: Number(category.valore || 0),
            note: form.note,
        });
        setLastEdited(null);
    };

    const acceptDisclaimer = async () => {
        setSaving(true);
        try {
            await apiPrivate.post("/market/disclaimer/accept");
            setStep("form");
            toast.success("Disclaimer accettato");
        } catch (e) { toast.error("Errore"); }
        finally { setSaving(false); }
    };

    const qtyNum = parseFloat(form.quantity);
    const valNum = parseFloat(form.valore_richiesto);
    const overMax = !isNaN(qtyNum) && qtyNum > maxPct + 1e-9;
    const invalid = isNaN(qtyNum) || qtyNum <= 0 || isNaN(valNum) || valNum < 0 || overMax;

    const publish = async () => {
        setSaving(true);
        try {
            await apiPrivate.post("/market/listings", {
                quota_category: category.categoria,
                quantity: Number(form.quantity),
                valore_richiesto: Number(form.valore_richiesto),
                numero_quote: form.quote_amount !== "" && !isNaN(parseFloat(form.quote_amount)) ? Number(form.quote_amount) : null,
                note: form.note || null,
                disclaimer_accepted: true, // già accettato nello step precedente
            });
            toast.success("Inserzione pubblicata sulla Bacheca annunci soci");
            onSuccess();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore pubblicazione");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sell-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-display tracking-tighter">Metti in vendita — Quota {category.categoria}</h3>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white">×</button>
                </div>

                {step === "disclaimer" ? (
                    <>
                        <p className="text-sm text-zinc-400 mb-3">Prima di procedere devi accettare il disclaimer della Bacheca annunci soci (versione <code className="text-bayside font-mono">{disclaimerMeta?.version || "…"}</code>):</p>
                        <div className="bg-zinc-900/40 rounded-xl p-4 text-xs text-zinc-300 max-h-64 overflow-y-auto whitespace-pre-wrap">{disclaimerText}</div>
                        {disclaimerMeta?.pdf_available && (
                            <button
                                onClick={() => downloadDisclaimerPdf(disclaimerMeta.pdf_filename)}
                                className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-grotesk text-magenta hover:text-white transition"
                                data-testid="sell-download-disclaimer-pdf"
                            >
                                <Download className="h-3.5 w-3.5" /> Scarica versione PDF firmata
                            </button>
                        )}
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={onClose} className="btn-ghost text-sm">Annulla</button>
                            <button onClick={acceptDisclaimer} disabled={saving} className="btn-rebel" data-testid="accept-disclaimer">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                Accetta e procedi
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="bento p-3 mb-3 text-xs text-zinc-400 flex items-start gap-2">
                            <TrendingUp className="h-4 w-4 text-bayside shrink-0 mt-0.5" />
                            <div>
                                <div>Riferimento intestatario: <span className="text-white">{fmtPct(maxPct)}</span> · <span className="text-white">{fmtQuote(category.valore)} quote</span></div>
                                <div className="text-[10px] text-zinc-500 mt-0.5">Prezzo unitario di riferimento: <span className="text-magenta">{fmtEur(unitPrice)}</span> per 1% di quota.</div>
                            </div>
                        </div>
                        <p className="text-xs text-zinc-500 mb-3">Compila uno qualsiasi dei tre campi (percentuale, numero quote, valore in €) — gli altri due vengono ricalcolati automaticamente sul prezzo unitario di riferimento. Puoi comunque sovrascriverli.</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-widest flex items-center justify-between">
                                    <span>Quantità (% possesso in vendita)</span>
                                    {lastEdited && lastEdited !== "quantity" && <span className="text-magenta normal-case tracking-normal text-[10px]">↳ calcolata</span>}
                                </label>
                                <input
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    max={maxPct}
                                    value={form.quantity}
                                    onChange={e => handleQuantityChange(e.target.value)}
                                    className="input-field"
                                    data-testid="sell-quantity"
                                />
                                {overMax && (
                                    <p className="text-vermillion text-[10px] mt-1">La quantità supera il tuo possesso ({fmtPct(maxPct)}).</p>
                                )}
                            </div>
                            <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-widest flex items-center justify-between">
                                    <span>Numero quote in vendita</span>
                                    {lastEdited && lastEdited !== "quote" && <span className="text-magenta normal-case tracking-normal text-[10px]">↳ calcolato</span>}
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={Number(category.valore || 0)}
                                    value={form.quote_amount}
                                    onChange={e => handleQuoteChange(e.target.value)}
                                    className="input-field"
                                    data-testid="sell-quote-amount"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-widest flex items-center justify-between">
                                    <span>Valore richiesto (€)</span>
                                    {lastEdited && lastEdited !== "valore" && <span className="text-magenta normal-case tracking-normal text-[10px]">↳ calcolato</span>}
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.valore_richiesto}
                                    onChange={e => handleValoreChange(e.target.value)}
                                    className="input-field"
                                    data-testid="sell-valore"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={resetToMax}
                                className="text-[11px] uppercase tracking-widest font-grotesk text-bayside hover:text-white transition"
                                data-testid="sell-reset-max"
                            >
                                Ripristina massimo cedibile
                            </button>
                            <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-widest">Note (opzionale)</label>
                                <textarea rows={3} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Es. disponibile a trattativa, minimo trattenuto, ecc." className="input-field" data-testid="sell-note" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={onClose} className="btn-ghost text-sm">Annulla</button>
                            <button onClick={publish} disabled={saving || invalid} className="btn-rebel" data-testid="publish-listing">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
                                Pubblica sulla Bacheca
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function CreateListingModal({ categories, listings, preemptions, listingByCat, preemptionByCat, disclaimerStatus, onClose, onSuccess, onRequestPreemption }) {
    // Categorie con disponibilità > 0 e senza already-listed (una categoria per volta)
    const initialSelectable = categories.find(c => {
        if (listingByCat[c.categoria]) return false;
        const av = computeAvailable(c, listings, preemptions);
        return av.remainingValue > 0;
    });
    const [selectedCat, setSelectedCat] = useState(initialSelectable?.categoria || categories[0]?.categoria || null);
    // pctOwned = % del capitale del SOCIO in questa categoria che si intende cedere (0-100)
    const [form, setForm] = useState({ pctOwned: "", quoteAmount: "", note: "" });
    const [disclaimerChecked, setDisclaimerChecked] = useState(false);
    const [saving, setSaving] = useState(false);
    const [busyPreemption, setBusyPreemption] = useState(false);

    const selected = categories.find(c => c.categoria === selectedCat);
    const availSelected = selected ? computeAvailable(selected, listings, preemptions) : { remainingValue: 0, usedValue: 0 };
    const maxValue = Number(availSelected.remainingValue || 0);
    // % del capitale del socio massima cedibile in questo annuncio, dato quanto già impegnato altrove
    const totalCatValue = selected ? Number(selected.valore || 0) : 0;
    const maxPctOwned = totalCatValue > 0 ? (maxValue / totalCatValue) * 100 : 0;

    // Precompila form quando cambia categoria: massimo cedibile
    useEffect(() => {
        if (!selected) return;
        setForm({
            pctOwned: +Number(maxPctOwned).toFixed(4),
            quoteAmount: +Number(maxValue).toFixed(2),
            note: "",
        });
        setDisclaimerChecked(false);
    }, [selectedCat]); // eslint-disable-line react-hooks/exhaustive-deps

    const stateOf = (cat) => {
        if (listingByCat[cat.categoria]) return { key: "listed", label: "Già in vendita", tone: "text-acid" };
        const av = computeAvailable(cat, listings, preemptions);
        if (av.remainingValue <= 0) return { key: "exhausted", label: "Nessuna disponibilità", tone: "text-vermillion" };
        if (cat.categoria === "A") {
            const p = preemptionByCat["A"];
            if (!p) return { key: "preemption-needed", label: "Serve prelazione", tone: "text-vermillion" };
            if (p.status === "pending") return { key: "preemption-pending", label: "Prelazione in attesa", tone: "text-magenta" };
            if (p.status === "rejected") return { key: "preemption-rejected", label: "Prelazione respinta", tone: "text-vermillion" };
            if (p.status === "approved") return { key: "sellable", label: "Vendibile", tone: "text-bayside" };
        }
        return { key: "sellable", label: "Vendibile", tone: "text-bayside" };
    };

    const resetToMax = () => {
        if (!selected) return;
        setForm(f => ({
            ...f,
            pctOwned: +Number(maxPctOwned).toFixed(4),
            quoteAmount: +Number(maxValue).toFixed(2),
        }));
    };

    // Importo € calcolato in tempo reale (visibile solo a chi crea l'annuncio, non nell'annuncio pubblico)
    const pctNum = parseFloat(form.pctOwned);
    const importoCalcolato = !isNaN(pctNum) && pctNum > 0 ? (pctNum / 100) * totalCatValue : 0;
    const overMax = !isNaN(pctNum) && pctNum > maxPctOwned + 1e-6;
    const formInvalid = !selected || isNaN(pctNum) || pctNum <= 0 || pctNum > 100 || overMax;

    // Invia richiesta di prelazione (con pct e importo calcolato)
    const requestPreemption = async () => {
        setBusyPreemption(true);
        try {
            await apiPrivate.post("/market/preemption", {
                quota_category: "A",
                quantity: !isNaN(pctNum) && pctNum > 0 ? pctNum : undefined,
                valore_richiesto: importoCalcolato > 0 ? +importoCalcolato.toFixed(2) : undefined,
                numero_quote: form.quoteAmount !== "" && !isNaN(parseFloat(form.quoteAmount)) ? Number(form.quoteAmount) : undefined,
            });
            toast.success("Richiesta di prelazione inviata — riceverai notifica quando sarà verificata");
            onRequestPreemption?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusyPreemption(false); }
    };

    const canPublish = !formInvalid && disclaimerChecked && !saving && stateOf(selected).key === "sellable";

    const publish = async () => {
        if (!canPublish) return;
        setSaving(true);
        try {
            await apiPrivate.post("/market/listings", {
                quota_category: selected.categoria,
                quantity: pctNum,
                valore_richiesto: +importoCalcolato.toFixed(2),
                numero_quote: form.quoteAmount !== "" && !isNaN(parseFloat(form.quoteAmount)) ? Number(form.quoteAmount) : null,
                note: form.note || null,
                disclaimer_accepted: true,
            });
            toast.success(`Annuncio Quota ${selected.categoria} pubblicato — disclaimer registrato`);
            onSuccess();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore pubblicazione");
        } finally { setSaving(false); }
    };

    const currentState = selected ? stateOf(selected) : null;

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="create-listing-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-3xl w-full p-7 my-8" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4 gap-3">
                    <div>
                        <span className="label-tag text-magenta">— Nuovo annuncio</span>
                        <h3 className="text-2xl font-display tracking-tighter mt-1">Crea un annuncio sulla Bacheca annunci soci</h3>
                        <p className="text-xs text-zinc-500 mt-1 font-grotesk">
                            Un annuncio per una quota alla volta. Seleziona la quota, imposta la percentuale del tuo capitale in vendita e accetta il disclaimer.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="create-listing-close">✕</button>
                </div>

                {/* Selettore quota */}
                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk mb-2">Seleziona la quota da mettere in vendita</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="quota-selector">
                        {categories.map(cat => {
                            const st = stateOf(cat);
                            const disabled = st.key !== "sellable";
                            const active = cat.categoria === selectedCat;
                            return (
                                <button
                                    key={cat.categoria}
                                    onClick={() => setSelectedCat(cat.categoria)}
                                    disabled={disabled && !active}
                                    className={`text-left p-3 rounded-xl border-2 transition ${active ? "border-magenta bg-magenta/10" : "border-zinc-800 hover:border-zinc-700"} ${disabled ? "opacity-50" : ""}`}
                                    data-testid={`select-quota-${cat.categoria}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="font-display text-lg">Quota {cat.categoria}</div>
                                        {active && <span className="text-magenta text-lg">●</span>}
                                    </div>
                                    <div className="text-[10px] text-zinc-500 mt-1 font-grotesk">{fmtQuote(cat.valore)} quote</div>
                                    <div className={`text-[10px] mt-1 font-grotesk ${st.tone}`}>{st.label}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Body dipendente dallo stato della quota selezionata */}
                {selected && currentState.key === "listed" && (
                    <div className="mt-6 bento p-5" data-testid="already-listed-block">
                        <div className="text-sm text-acid">Hai già un annuncio attivo per questa quota.</div>
                        <div className="text-xs text-zinc-500 mt-1">Ritira l&apos;inserzione esistente dalla lista &quot;Annunci pubblicati&quot; per crearne una nuova.</div>
                    </div>
                )}

                {selected && currentState.key === "exhausted" && (
                    <div className="mt-6 bento p-5" data-testid="exhausted-block">
                        <div className="text-sm text-vermillion">Hai già impegnato l&apos;intera quota tra annunci attivi e prelazioni in essere.</div>
                        <div className="text-xs text-zinc-500 mt-1">Ritira un annuncio o attendi la risoluzione delle prelazioni per liberare disponibilità.</div>
                    </div>
                )}

                {selected && currentState.key === "preemption-pending" && (
                    <div className="mt-6 bento p-5" data-testid="preemption-pending-block">
                        <div className="flex items-center gap-2 text-sm text-magenta">
                            <Clock className="h-4 w-4" /> Prelazione in attesa di verifica ({fmtPct(preemptionByCat["A"]?.quantity)}). Torna qui una volta approvata.
                        </div>
                    </div>
                )}

                {/* Stati che richiedono l'inserimento della percentuale: sellable, preemption-needed, preemption-rejected */}
                {selected && ["sellable", "preemption-needed", "preemption-rejected"].includes(currentState.key) && (
                    <>
                        {/* Disponibilità residua */}
                        <div className="mt-6 bento p-3 text-xs text-zinc-400 flex items-start gap-2" data-testid="modal-availability">
                            <TrendingUp className="h-4 w-4 text-bayside shrink-0 mt-0.5" />
                            <div>
                                <div>Quote libere per questo annuncio: <span className="text-bayside font-mono">{fmtQuote(maxValue)}</span></div>
                                <div className="text-[10px] text-zinc-500 mt-0.5">
                                    Numero quote totali in Quota {selected.categoria}: <span className="text-white">{fmtQuote(totalCatValue)}</span> · Già impegnate in annunci/prelazioni: {fmtQuote(availSelected.usedValue)}
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-widest">
                                    Percentuale del tuo capitale in vendita (%)
                                </label>
                                <input
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    max="100"
                                    value={form.pctOwned}
                                    onChange={e => {
                                        const v = e.target.value;
                                        const n = parseFloat(v);
                                        setForm(f => ({
                                            ...f,
                                            pctOwned: v,
                                            quoteAmount: !isNaN(n) && n >= 0 && totalCatValue > 0
                                                ? +((n / 100) * totalCatValue).toFixed(2)
                                                : "",
                                        }));
                                    }}
                                    className="input-field"
                                    data-testid="create-pct-owned"
                                    placeholder="Es. 50 (metà del tuo capitale)"
                                />
                                <p className="text-[11px] text-zinc-500 mt-1 font-grotesk">
                                    100% = metti in vendita l&apos;intero tuo capitale investito in questa quota.
                                </p>
                            </div>
                            <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-widest">
                                    Numero quote in vendita
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max={totalCatValue || undefined}
                                    value={form.quoteAmount ?? ""}
                                    onChange={e => {
                                        const v = e.target.value;
                                        const n = parseFloat(v);
                                        setForm(f => ({
                                            ...f,
                                            quoteAmount: v,
                                            pctOwned: !isNaN(n) && n >= 0 && totalCatValue > 0
                                                ? +((n / totalCatValue) * 100).toFixed(4)
                                                : "",
                                        }));
                                    }}
                                    className="input-field"
                                    data-testid="create-quote-amount"
                                    placeholder={`Max: ${fmtQuote(totalCatValue)}`}
                                />
                                <p className="text-[11px] text-zinc-500 mt-1 font-grotesk">
                                    Equivale a {form.pctOwned && !isNaN(parseFloat(form.pctOwned)) ? `${Number(form.pctOwned).toFixed(4)}%` : "—"} del tuo capitale.
                                </p>
                            </div>
                        </div>
                        {overMax && <p className="text-vermillion text-[10px] mt-1">La percentuale supera la disponibilità residua ({Number(maxPctOwned).toFixed(4)}%).</p>}

                        {/* Preview della percentuale scelta (info per chi crea) */}
                        {!isNaN(pctNum) && pctNum > 0 && !overMax && (
                            <div className="mt-3 p-3 rounded-xl bg-magenta/5 border border-magenta/20" data-testid="pct-preview">
                                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">Quantità in vendita sull&apos;annuncio</div>
                                <div className="text-2xl font-display text-magenta mt-1">{Number(pctNum).toFixed(4)}%</div>
                                <div className="text-[10px] text-zinc-500 mt-1 font-grotesk">
                                    Sarà l&apos;unico dato visibile agli altri soci nell&apos;annuncio pubblico. Nessun importo in € verrà mostrato.
                                </div>
                            </div>
                        )}

                        <button type="button" onClick={resetToMax} className="text-[11px] uppercase tracking-widest font-grotesk text-bayside hover:text-white transition mt-2" data-testid="create-reset-max">
                            Ripristina massimo disponibile
                        </button>
                        <div className="mt-3">
                            <label className="text-xs text-zinc-500 uppercase tracking-widest">Note (opzionale)</label>
                            <textarea rows={2} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Es. disponibile a trattativa, minimo trattenuto, ecc." className="input-field" data-testid="create-note" />
                        </div>

                        {/* Ramo A: sellable — disclaimer + publish */}
                        {currentState.key === "sellable" && (
                            <>
                                <div className="mt-5 bento p-4 border-l-4 border-l-magenta" data-testid="create-disclaimer-block">
                                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk mb-2">Disclaimer obbligatorio · versione <span className="text-magenta">{disclaimerStatus?.version}</span></div>
                                    {disclaimerStatus?.pdf_available ? (
                                        <button
                                            onClick={() => downloadDisclaimerPdf(disclaimerStatus.pdf_filename)}
                                            className="btn-ghost text-xs w-full justify-center"
                                            data-testid="create-disclaimer-download"
                                        >
                                            <Download className="h-4 w-4" /> Scarica e leggi il PDF del disclaimer
                                        </button>
                                    ) : (
                                        <p className="text-xs text-vermillion italic">Il PDF del disclaimer non è ancora stato caricato dall&apos;amministrazione. Contatta il team per procedere alla pubblicazione.</p>
                                    )}
                                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={disclaimerChecked}
                                            onChange={e => setDisclaimerChecked(e.target.checked)}
                                            disabled={!disclaimerStatus?.pdf_available}
                                            className="mt-1 accent-magenta h-4 w-4"
                                            data-testid="create-disclaimer-check"
                                        />
                                        <span className="text-xs text-zinc-300">
                                            Dichiaro di aver scaricato, letto e accettato il PDF del disclaimer ufficiale. Confermo che la Società non interviene nella trattativa e non garantisce prezzi, quantità o esito della cessione. <strong className="text-white">La data di accettazione verrà registrata e associata a questo annuncio.</strong>
                                        </span>
                                    </label>
                                </div>
                                <div className="flex justify-end gap-2 mt-6">
                                    <button onClick={onClose} className="btn-ghost text-sm" data-testid="create-cancel">Annulla</button>
                                    <button onClick={publish} disabled={!canPublish} className="btn-rebel" data-testid="create-publish">
                                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
                                        Pubblica annuncio Quota {selected.categoria}
                                    </button>
                                </div>
                                {!disclaimerChecked && !formInvalid && (
                                    <p className="text-[11px] text-vermillion mt-2 text-right font-grotesk">Devi accettare il disclaimer per pubblicare</p>
                                )}
                            </>
                        )}

                        {/* Ramo B: preemption-needed — nessun disclaimer, solo invio richiesta */}
                        {currentState.key === "preemption-needed" && (
                            <>
                                <div className="mt-5 bento p-4 border-l-4 border-l-vermillion" data-testid="preemption-block">
                                    <div className="flex items-start gap-2 text-sm">
                                        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-vermillion" />
                                        <div>
                                            <strong className="text-white">Prelazione statutaria richiesta per la Quota A.</strong>
                                            <p className="text-xs text-zinc-400 mt-1">Le Quote A non possono essere pubblicate direttamente sulla Bacheca annunci soci. Confermando invierai la tua richiesta all&apos;amministrazione per la quantità scelta ({fmtPct(pctNum)}). Riceverai notifica quando la prelazione sarà verificata.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-6">
                                    <button onClick={onClose} className="btn-ghost text-sm" data-testid="create-cancel">Annulla</button>
                                    <button onClick={requestPreemption} disabled={busyPreemption || formInvalid} className="btn-rebel" data-testid="modal-request-preemption">
                                        {busyPreemption ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                                        Invia richiesta di prelazione
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Ramo C: preemption-rejected — motivo + retry */}
                        {currentState.key === "preemption-rejected" && (
                            <>
                                <div className="mt-5 bento p-4 border-l-4 border-l-vermillion" data-testid="preemption-rejected-block">
                                    <div className="flex items-start gap-2 text-sm">
                                        <XCircle className="h-5 w-5 shrink-0 mt-0.5 text-vermillion" />
                                        <div>
                                            <strong className="text-white">Richiesta di prelazione precedente respinta dall&apos;amministrazione.</strong>
                                            {preemptionByCat["A"]?.admin_note && (
                                                <div className="text-zinc-400 mt-1"><span className="text-zinc-500">Motivo:</span> {preemptionByCat["A"].admin_note}</div>
                                            )}
                                            {preemptionByCat["A"]?.resolved_at && (
                                                <div className="text-zinc-600 mt-1 text-xs">Respinta il {fmtDate(preemptionByCat["A"].resolved_at)}</div>
                                            )}
                                            <p className="text-xs text-zinc-400 mt-2">Puoi inviare una nuova richiesta con quantità aggiornata ({fmtPct(pctNum)}).</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-6">
                                    <button onClick={onClose} className="btn-ghost text-sm" data-testid="create-cancel">Annulla</button>
                                    <button onClick={requestPreemption} disabled={busyPreemption || formInvalid} className="btn-rebel" data-testid="modal-re-request-preemption">
                                        {busyPreemption ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                                        Invia nuova richiesta di prelazione
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

